<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { compress as compressPage } from '@/routes/tools/pdf';
import compressTool from '@/routes/tools/pdf/compress';

const { lineUpTo } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.compress.seo',
    fallbackTitleKey: 'tools.pdf.compress.title',
    fallbackTitleLabel: 'Compress PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: compressPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.compress.title',
                title: 'Compress PDF',
                href: compressPage.url(),
            },
        ],
    },
});

type Level = 'low' | 'medium' | 'high';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const level = ref<Level>('medium');
const status = ref<Status>('idle');
const statusMessage = ref('');

const levelOptions = computed(() => {
    type Opt = { label: string; value: Level; desc: string };

    const opts: Opt[] = [
        {
            label: t('tools.pdf.ui.quality_low', 'Low'),
            value: 'low',
            desc: t('tools.pdf.ui.compress_desc_low', 'Smallest file size'),
        },
        {
            label: t('tools.pdf.ui.quality_medium', 'Medium'),
            value: 'medium',
            desc: t('tools.pdf.ui.compress_desc_medium', 'Balanced quality'),
        },
        {
            label: t('tools.pdf.ui.quality_high', 'High'),
            value: 'high',
            desc: t('tools.pdf.ui.compress_desc_high', 'Best quality'),
        },
    ];

    return opts;
});

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();
            const msg =
                (messages[0] as string) ||
                t('tools.pdf.ui.compress_failed', 'Compression failed.');

            if (msg.toLowerCase().includes('ghostscript')) {
                return t(
                    'tools.pdf.ui.ghostscript_missing',
                    'GhostScript is not installed on this server. Please contact the administrator.',
                );
            }

            return msg;
        } catch {
            return t('tools.pdf.ui.compress_failed', 'Compression failed.');
        }
    }

    return t('tools.pdf.ui.compress_failed', 'Compression failed.');
}

async function compress(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.compressing', 'Compressing PDF...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('level', level.value);

    try {
        const response = await axios.post(compressTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'compressed.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.compressed_success',
            'PDF compressed successfully!',
        );
        file.value = null;
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-compress" />

    <ToolLayout
        :title="t('tools.pdf.compress.title', 'Compress PDF')"
        :subtitle="t('tools.pdf.compress.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :label="
                        t('tools.pdf.ui.drop_pdf', 'Click or drag a PDF here')
                    "
                    :sublabel="
                        lineUpTo(t('tools.pdf.ui.one_pdf_file', 'One PDF file'))
                    "
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard :file="file" @remove="file = null" />
                </div>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <p
                    class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {{
                        t(
                            'tools.pdf.ui.compress_level_title',
                            'Compression Level',
                        )
                    }}
                </p>
                <div class="flex gap-2">
                    <button
                        v-for="opt in levelOptions"
                        :key="opt.value"
                        type="button"
                        class="flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
                        :class="
                            level === opt.value
                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                        "
                        @click="level = opt.value"
                    >
                        <span class="block">{{ opt.label }}</span>
                        <span
                            class="text-xs text-gray-400 dark:text-gray-500"
                            >{{ opt.desc }}</span
                        >
                    </button>
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || status === 'loading'"
                @click="compress"
            >
                {{ t('tools.pdf.ui.compress_pdf_button', 'Compress PDF') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
